import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Info, History, ArrowUpRight, Sparkles, Layers, Gem, MapPin, ArrowRight } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import usePageSEO, { makeServiceSchema } from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import RelatedServices from "@/components/RelatedServices";
import { laminateHubFAQs } from "@/data/faqData";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import heroImg from "@/assets/waterproof-laminate-entryway-hero.webp";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const LaminateFlooring = () => {
  usePageSEO({
    title: "Waterproof Laminate Flooring | Quick-Step Authorized Dealer",
    description: "Waterproof laminate flooring at Michigan's Quick-Step authorized dealer — Studio, NatureTEK Plus, Select, Premier. Lifetime warranty.",
    path: "/laminate-flooring",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        makeServiceSchema(
          "Waterproof Laminate Flooring Michigan",
          "Quick-Step certified since 1998. Uniclic locking technology, flush-mount stair nosings, Ali Trims and TOP Step flush-mount vents."
        ),
        {
          "@type": "ItemList",
          "name": "Quick-Step Collections & Services",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Quick-Step NatureTEK Plus", "url": "https://1woodfloors.com/brands/quick-step/naturetek-plus" },
            { "@type": "ListItem", "position": 2, "name": "Quick-Step NatureTEK Premier", "url": "https://1woodfloors.com/brands/quick-step/naturetek-premier" },
            { "@type": "ListItem", "position": 3, "name": "Quick-Step NatureTEK Select", "url": "https://1woodfloors.com/brands/quick-step/naturetek-select" },
            { "@type": "ListItem", "position": 4, "name": "Quick-Step Studio", "url": "https://1woodfloors.com/brands/quick-step/studio" },
            { "@type": "ListItem", "position": 5, "name": "Laminate Installation", "url": "https://1woodfloors.com/laminate-installation" },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ══ HERO ══ */}
        <section className="relative min-h-[560px] max-h-[800px] h-[75svh] flex items-end overflow-hidden bg-foreground">
          <PageBreadcrumb variant="overlay" items={[
            { label: "Home", to: "/" },
            { label: "Waterproof Laminate Flooring" },
          ]} />
          <div className="absolute inset-0">
            <img width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async"
              src={heroImg}
              alt="Waterproof laminate flooring installation in Michigan MI 1 Wood Floors"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/50 to-foreground/20" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
          >
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Waterproof Laminate Flooring
            </p>
            <h1 className="font-display text-[clamp(28px,5.5vw,58px)] font-semibold leading-[1.08] tracking-tight text-primary-foreground">
              Waterproof Laminate Flooring Michigan
            </h1>
            <p className="font-display text-[clamp(18px,3vw,30px)] font-light text-primary-foreground/70 mt-2 leading-[1.2]">
              Michigan's First Certified Pergo Installer  Since 1998
            </p>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              Quick-Step certified since 1998. One brand, mastered completely. Uniclic technology, flush-mount stair nosings and architectural finish details.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                    Book a $50 Estimate Visit
                  </button>
                }
              />
              <a href="tel:2483258853" className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center">
                Call (248) 325-8853
              </a>
            </div>
          </motion.div>
        </section>

        {/* ══ SECTION 1 — FORENSIC FAST-FACT BOX ══ */}
        <section className="bg-background py-14 md:py-20">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}
              className="border border-primary/30 bg-primary/[.04] rounded-2xl p-6 md:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <Info className="w-6 h-6 text-primary flex-shrink-0" />
                <h2 className="font-display text-lg md:text-xl font-semibold text-foreground">Laminate Forensic Fast-Facts</h2>
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                {[
                  ["Waterproof Laminate", "A multi-layer floor with a high-density fiberboard (HDF) core sealed against moisture, a photographic wood layer, and a wear-resistant surface — installed as a floating floor"],
                  ["Uniclic Technology", "Quick-Step's patented locking mechanism — the most precise click-lock system in the laminate category"],
                  ["Wear Layer", "The protective surface of the laminate — these floors do not wear, stain, or fade the way real wood does"],
                  ["Flush-Mount Nosing", "A stair nosing that sits level with the tread surface rather than overlapping it — eliminates the raised-edge look on staircases"],
                ].map(([term, def]) => (
                  <div key={term}>
                    <dt className="text-sm font-semibold text-foreground mb-1">{term}</dt>
                    <dd className="text-sm text-muted-foreground leading-relaxed">{def}</dd>
                  </div>
                ))}
              </dl>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 2 — THE HERITAGE: FROM PERGO 1998 TO QUICK-STEP TODAY ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">25 Year Legacy</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                The Heritage: From Pergo 1998 to Quick-Step Today
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <History className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-primary-foreground mb-2">Where It Started</h3>
                    <p className="text-sm text-primary-foreground/70 leading-relaxed">
                      Our relationship with laminate flooring goes back to 1998, when we were endorsed with Pergo — back when these floors were still glued together plank by plank. We watched and worked through every evolution: glued construction to click-lock, standard wear to commercial-grade finishes, basic visuals to the high-definition photographic surfaces available today.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ArrowUpRight className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-primary-foreground mb-2">Where It Is Now</h3>
                    <p className="text-sm text-primary-foreground/70 leading-relaxed">
                      When Quick-Step patented the Uniclic locking technology — widely regarded as the most precise and durable click-lock mechanism in the category — we became certified installers. Today we carry{" "}
                      <Link to="/brands/quick-step" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                        Quick-Step NatureTEK waterproof collections
                      </Link>
                      {" "}exclusively because we believe in depth over breadth. One brand, mastered completely.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ SECTION 3 — EXPONENTIAL PERFORMANCE ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Performance Engineering</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                Exponential Performance
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Modern waterproof laminate offers something real hardwood cannot: immunity to wear, staining, and fading. Many Quick-Step collections carry lifetime warranties. For high-traffic areas — mudrooms, main hallways, family rooms with kids and pets — laminate can be the smarter long-term choice.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The wood look is preserved permanently because it is a photographic layer protected by a hardened wear surface, not an actual wood surface subject to the natural aging of timber.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-xs font-semibold tracking-wide uppercase text-primary">
                    Lifetime residential warranty on Quick-Step NatureTEK collections
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 4 — THE SEAMLESS STAIRCASE ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Staircase Integration</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                The Seamless Staircase
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Most laminate installations treat staircases as an afterthought — a standard overlap nosing that creates a raised edge at every tread. We eliminate that. Quick-Step manufactures flush-mount stair nosing components specifically designed to sit level with the tread surface, creating a continuous, architectural stair profile with no raised edges, no trip points, and no visual interruption.
                </p>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Combined with{" "}
                  <a href="https://www.stairrods.us/ali-trims" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    Ali Trims European transition profiles
                  </a>
                  {" "}and TOP Step flush-mount vents, a laminate staircase and floor installation by 1 Wood Floors reads as a single designed system — not a collection of separate contractor decisions.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 5 — THE FINISHING DETAILS: MID-RANGE TO MASTERPIECE ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">The 1 Wood Floors Difference</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Finishing Details: Mid-Range to Masterpiece
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl mb-8">
                The difference between a laminate floor that looks like a laminate floor and one that looks like a premium architectural installation is entirely in the details. We apply the same finishing standard to every laminate project:
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: <Layers className="w-7 h-7 text-primary" />, title: "Flush-Mount Stair Nosings", desc: "Quick-Step flush-mount stair nosings — level with the tread, no overlap" },
                { icon: <Gem className="w-7 h-7 text-primary" />, title: "Ali Trims Transitions", desc: "European-sourced metal transition profiles — architectural quality at every doorway" },
                { icon: <Sparkles className="w-7 h-7 text-primary" />, title: "TOP Step Flush-Mount Vents", desc: "Grain and color matched to the laminate surface, sitting flush rather than sitting on top" },
              ].map((item, i) => (
                <motion.div key={item.title} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="border border-border rounded-2xl p-6 md:p-8"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="font-display text-base font-semibold text-foreground mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.4 }} className="mt-8 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
              <p className="text-sm font-semibold text-foreground italic">
                These details cost a fraction of the floor itself. They make the floor look like it cost twice as much.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 6 — INSTALLATION SERVICE CTA ══ */}
        <section className="bg-primary py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
                Certified Quick-Step installation from <em className="italic font-normal text-[hsl(var(--gold-300))]">subfloor to finish.</em>
              </h3>
              <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
                Certified Quick-Step installation from subfloor prep through architectural finish details.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
              <Link
                to="/laminate-installation"
                className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center"
              >
                View Our Laminate Installation Process →
              </Link>
              <a href="tel:2483258853" className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center">
                📞 (248) 325-8853
              </a>
            </div>
          </div>
        </section>

        {/* ══ SECTION 7 — BRANDS WE CARRY ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Certified Installer</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-8">
                Brands We Carry
              </h2>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.5, delay: 0.1 }}
              className="border border-border rounded-2xl p-6 md:p-8"
            >
              <Link to="/brands/quick-step" className="group block">
                <h3 className="font-display text-lg font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">Quick-Step</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Michigan's first certified Pergo installer. Uniclic technology, lifetime warranties, four NatureTEK waterproof collections.
                </p>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary group-hover:text-[hsl(var(--gold-300))] transition-colors">
                  Explore Quick-Step <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { name: "NatureTEK Plus", to: "/brands/quick-step/naturetek-plus" },
                  { name: "NatureTEK Premier", to: "/brands/quick-step/naturetek-premier" },
                  { name: "NatureTEK Select", to: "/brands/quick-step/naturetek-select" },
                  { name: "Studio", to: "/brands/quick-step/studio" },
                ].map((c) => (
                  <Link key={c.name} to={c.to} className="px-4 py-2.5 rounded-xl border border-border text-xs font-medium text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors text-center">
                    {c.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 8 — RELATED SERVICES ══ */}
        <RelatedServices
          heading="Complete your laminate project."
          subheading="Related Services"
          links={[
            { title: "Subfloor Leveling & Repair", desc: "Laminate requires a flat subfloor — this is how we engineer it", to: "/subfloor-leveling", tag: "Foundation" },
            { title: "Environment & Acclimation", desc: "Laminate still needs to acclimate to your home's climate", to: "/environment-acclimation", tag: "Science" },
            { title: "NWFA Certified Inspections", desc: "Independent analysis for laminate failures and warranty disputes", to: "/inspections", tag: "Inspections" },
          ]}
        />

        {/* ══ SECTION 9 — SERVICE AREAS ══ */}
        <section className="bg-background py-14 md:py-20">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary">Service Areas</p>
              </div>
              <h2 className="font-display text-[clamp(22px,3.5vw,34px)] font-semibold leading-[1.12] text-foreground mb-6">
                Laminate Installation Across Michigan
              </h2>
            </motion.div>

            <div className="flex flex-wrap gap-3">
              {[
                { name: "Livonia", to: "/" },
                { name: "Northville", to: "/hardwood-flooring/northville-mi" },
                { name: "Plymouth", to: "/hardwood-flooring/plymouth-mi" },
                { name: "Ann Arbor", to: "/hardwood-flooring/ann-arbor-mi" },
                { name: "Birmingham", to: "/hardwood-flooring/birmingham-mi" },
              ].map((city) => (
                <Link key={city.name} to={city.to} className="px-5 py-2.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                  {city.name}
                </Link>
              ))}
            </div>
            <Link to="/hardwood-flooring" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-primary hover:text-[hsl(var(--gold-300))] transition-colors">
              View all 29 Michigan service areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ══ SECTION 10 — REVIEWS ══ */}
        <GoogleReviewsCarousel contextTags={["installation"]} count={4} />

        {/* ══ SECTION 11 — FAQ ══ */}
        <PageFAQ suppressJsonLd
          faqs={laminateHubFAQs}
          heading={<>Laminate flooring questions <em className="italic font-normal text-primary">answered.</em></>}
          subheading="Waterproof Laminate FAQs"
        />
      </main>
      <SiteFooter />
    </div>
  );
};

export default LaminateFlooring;
